import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BackButton from './BackButton';
import LanguageSelector from './LanguageSelector';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const bmweLogo = i18n.language === 'en' ? '/bmwe-logo-en.svg' : '/bmwe-logo.svg';

  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');

  const isLoggedIn = Boolean(localStorage.getItem('jwt'));
  const isHome = location.pathname === '/' || location.pathname.match(/^\/[^/]+$/) && !['/signup', '/login', '/forgot-password', '/reset-password', '/email-confirmed', '/company', '/edit-profile'].includes(location.pathname);

  const showBack = !isHome;
  const showEditProfile = isLoggedIn && location.pathname !== '/edit-profile';
  const showCompanyForm = isLoggedIn && location.pathname !== '/company';
  const showLogout = isLoggedIn;

  const navButtonSx = {
    borderColor: '#0063a6',
    color: '#0063a6',
    borderRadius: 0,
    borderWidth: 2,
    fontWeight: 600,
    fontSize: '0.85rem',
    px: 2.5,
    '&:hover': {
      borderColor: '#94c11c',
      color: '#94c11c',
      backgroundColor: 'rgba(148, 193, 28, 0.06)',
      borderWidth: 2,
    },
  };

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: '#fff',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: { xs: 1.5, sm: 3 },
        py: 1,
        borderBottom: '1px solid #dce1e8',
        flexShrink: 0,
      }}
    >
      <Box display="flex" alignItems="center" gap={isMobile ? 1 : 2} sx={{ flex: '0 0 auto' }}>
        {showBack && <BackButton />}
        <img src="/trasaar-logo.svg" alt="TraSaar" style={{ height: isMobile ? 52 : 78 }} />
        <img src={bmweLogo} alt={i18n.language === 'en' ? 'Funded by BMWE' : 'Gefördert durch BMWE'} style={{ height: isMobile ? 105 : 135 }} />
      </Box>

      {!isMobile && isHome && (
        <Typography
          sx={{
            flex: 1,
            textAlign: 'center',
            fontWeight: 700,
            color: '#0063a6',
            fontFamily: '"nowaybold", "nowayregular", sans-serif',
            fontSize: '1.4rem',
            whiteSpace: 'nowrap',
          }}
        >
          {t('site_title')}
        </Typography>
      )}

      <Box display="flex" alignItems="center" gap={1.5} sx={{ flex: '0 0 auto' }}>
        <LanguageSelector />
        {!isMobile ? (
          <Box display="flex" gap={1.5}>
            {showCompanyForm && (
              <Button variant="outlined" onClick={() => navigate('/company')} sx={navButtonSx}>
                {t('company_form.title')}
              </Button>
            )}
            {showEditProfile && (
              <Button variant="outlined" onClick={() => navigate('/edit-profile')} sx={navButtonSx}>
                {t('edit_profile.title')}
              </Button>
            )}
            {showLogout && (
              <Button
                variant="outlined"
                onClick={() => { localStorage.removeItem('jwt'); localStorage.removeItem('user'); navigate('/login'); }}
                sx={{ ...navButtonSx, borderColor: '#cf2e2e', color: '#cf2e2e', '&:hover': { borderColor: '#a82424', backgroundColor: 'rgba(207, 46, 46, 0.06)', borderWidth: 2 } }}
              >
                {t('edit_profile.logout')}
              </Button>
            )}
          </Box>
        ) : (
          isLoggedIn && (
            <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: '#0063a6' }}>
              <MenuIcon />
            </IconButton>
          )
        )}
      </Box>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { borderRadius: 0, width: 280 } }}
      >
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <img src="/trasaar-logo.svg" alt="TraSaar" style={{ height: 32 }} />
            <IconButton onClick={() => setDrawerOpen(false)} size="small" sx={{ color: '#666' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <List sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {showCompanyForm && (
              <ListItem disablePadding>
                <Button fullWidth variant="outlined" onClick={() => { navigate('/company'); setDrawerOpen(false); }} sx={{ ...navButtonSx, justifyContent: 'center' }}>
                  {t('company_form.title')}
                </Button>
              </ListItem>
            )}
            {showEditProfile && (
              <ListItem disablePadding>
                <Button fullWidth variant="outlined" onClick={() => { navigate('/edit-profile'); setDrawerOpen(false); }} sx={{ ...navButtonSx, justifyContent: 'center' }}>
                  {t('edit_profile.title')}
                </Button>
              </ListItem>
            )}
            {showLogout && (
              <ListItem disablePadding>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => { localStorage.removeItem('jwt'); localStorage.removeItem('user'); navigate('/login'); setDrawerOpen(false); }}
                  sx={{ ...navButtonSx, borderColor: '#cf2e2e', color: '#cf2e2e', justifyContent: 'center', '&:hover': { borderColor: '#a82424', backgroundColor: 'rgba(207, 46, 46, 0.06)', borderWidth: 2 } }}
                >
                  {t('edit_profile.logout')}
                </Button>
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Header;
